import React, { ComponentType, useLayoutEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import TrackingOp from '../../components/TrackingOp';
import { CalendarProvider } from '../../contexts/CalendarContext';
import { selectViewMode } from '../../util/store/selectors';
import { AppDispatch, useSelector } from '../../util/store/store';
import { VESSEL_DEVICE_TYPE_NAME } from '../../util/varibles/constants';
import { IOperation, IVessel } from '../../util/varibles/interface';
import { VIEW_MODE } from '../../util/varibles/permission';
import { SOCKET_FUNC, SOCKET_NAME } from '../../web-socket/constants';
import WSService, { IValueListener } from '../../web-socket/web-socket-service';
import Calendar from './Calendar';
import CalendarSite from './CalendarSite';
import CalendarVessel from './CalendarVessel';
import { overviewActions } from './reducer';
import styles from './style.module.scss';

const contentView: Partial<Record<VIEW_MODE, ComponentType>> = {
    [VIEW_MODE.PLANNER]: Calendar,
    [VIEW_MODE.VESSEL]: CalendarVessel,
    [VIEW_MODE.SITE]: CalendarSite,
};

const Body = () => {
    const viewMode = useSelector(selectViewMode) as VIEW_MODE;
    const ViewComponent = contentView[viewMode];

    if (!ViewComponent) return null;

    return <CalendarProvider>
        <ViewComponent />
    </CalendarProvider>;
};

const WrapperTrackingOp = () => {
    const viewMode = useSelector(selectViewMode) as VIEW_MODE;
    const collapsed = useSelector(state => state.overview.showAllPlans);
    const dispatch = useDispatch();

    return viewMode === VIEW_MODE.VESSEL
        ? <TrackingOp
            collapsed={collapsed}
            onExpand={() => dispatch(overviewActions.setShowAllPlans(false))}
            hideLeftBorder
        />
        : null;
};

type VesselResponse = { vessel: Pick<IVessel, 'id'> };

const OverviewPage = () => {
    const dispatch = useDispatch<AppDispatch>();
    const groupIdsRef = useRef<string[]>([]);

    useLayoutEffect(() => {
        let isActive = true;

        dispatch(overviewActions.initialDataRequest({
            action: (vessels: VesselResponse[]) => {
                if (!isActive || vessels.length === 0) return;

                const groupIds = vessels.flatMap(({ vessel }) => [
                    vessel.id,
                    `${vessel.id}-${VESSEL_DEVICE_TYPE_NAME.LOCATION}`,
                ]);
                groupIdsRef.current = groupIds;

                new WSService({
                    groups: groupIds,
                    listener: {
                        lev: SOCKET_NAME.PAGE,
                        func: (value: IValueListener) => {
                            switch (value.function) {
                                case SOCKET_FUNC.OPERATION_STATUS_CHANGE:
                                case SOCKET_FUNC.OPERATION_STATE_CHANGE:
                                    dispatch(overviewActions.updateOps(value.message));
                                    dispatch(overviewActions.updateOpsSiteByWs(value.message));
                                    break;
                                case SOCKET_FUNC.ONGOING_UPDATE: {
                                    const operations = Array.isArray(value.message?.operations)
                                        ? value.message.operations
                                        : [];
                                    dispatch(overviewActions.updateOps(operations));
                                    dispatch(overviewActions.updateOpsSiteByWs(operations));
                                    break;
                                }
                                case SOCKET_FUNC.OPERATION_DELETED:
                                    dispatch(overviewActions.deleteOps(value.message));
                                    dispatch(overviewActions.deleteOpsSiteByWs(value.message));
                                    break;
                                case SOCKET_FUNC.OPERATION_CONFIRMED:
                                    dispatch(overviewActions.addOps(value.message as IOperation[]));
                                    dispatch(overviewActions.updateOpsSiteByWs(value.message));
                                    break;
                                default:
                                    break;
                            }
                        },
                    },
                });
            },
        }));

        return () => {
            isActive = false;
            WSService.close(SOCKET_NAME.PAGE, groupIdsRef.current);
            groupIdsRef.current = [];
        };
    }, [dispatch]);

    return <div className={`m-header bgPage ${styles.page}`}>
            <div className={styles.wrapper}>
            <div className={styles.content}>
                <Body />
            </div>
            <WrapperTrackingOp />
        </div>
    </div>;
};

export default OverviewPage;
